/**
 * ============================================================================
 * SalesOrder 模块 API 工厂函数
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义 SalesOrder（销售订单）模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * 🎯 核心概念：工厂模式
 * - 工厂函数：createSalesOrderApi(client) - 接收请求客户端，返回 API 方法集合
 * - 依赖注入：不依赖具体的请求实现（axios、uni.request 等）
 * - 平台无关：Web 端和 App 端都可以使用，只需提供不同的适配器
 * 
 * 💡 设计理念：
 * - "给我一个能发送请求的工具，我就能为你生成所有 API 方法"
 * - 请求客户端是"工具"，API 方法是"产品"，工厂函数是"生产线"
 * 
 * 📖 使用方式：
 * 
 * **Web 端（Vben Admin + axios）**：
 * ```typescript
 * // 1. 创建适配器（让 Vben 的 requestClient 符合 IRequestClient 接口）
 * import { requestClient } from '#/api/request'
 * import type { IRequestClient } from '@bayes-mes/shared/api/core/client'
 * 
 * const webAdapter: IRequestClient = {
 *   get: (url, config) => requestClient.get(url, config).then(res => res.data),
 *   post: (url, data, config) => requestClient.post(url, data, config).then(res => res.data),
 *   // ... 其他方法
 * }
 * 
 * // 2. 调用工厂函数，传入适配器
 * import { createSalesOrderApi } from '@bayes-mes/shared/api/sales-order/factory'
 * export const salesOrderApi = createSalesOrderApi(webAdapter)
 * 
 * // 3. 使用生成的 API
 * const result = await salesOrderApi.getSalesOrderList({ page: 1 })
 * ```
 * 
 * **App 端（uni-app X）**：
 * ```typescript
 * // 1. 创建适配器（让 uni.request 符合 IRequestClient 接口）
 * const uniAdapter: IRequestClient = {
 *   get: (url, config) => new Promise((resolve, reject) => {
 *     uni.request({
 *       url,
 *       method: 'GET',
 *       data: config?.params,
 *       success: (res) => resolve(res.data.data),
 *       fail: reject
 *     })
 *   }),
 *   // ... 其他方法
 * }
 * 
 * // 2. 调用工厂函数
 * import { createSalesOrderApi } from '@bayes-mes/shared/api/sales-order/factory'
 * export const salesOrderApi = createSalesOrderApi(uniAdapter)
 * ```
 * 
 * ⚠️ 注意事项：
 * - **请求客户端接口**：所有工厂函数都使用 `api/core/client.ts` 中定义的 `IRequestClient` 接口
 * - **不要重复定义接口**：从 `../core/client` 导入，不要在每个模块中重新定义
 * - **适配器职责**：适配器负责将平台特定的请求客户端转换为符合 `IRequestClient` 的对象
 * - **响应数据解包**：适配器需要处理响应数据的解包（如 `res.data.data`）
 * - **类型安全**：所有方法都有完整的 TypeScript 类型定义
 * - **注释详细**：每个方法都有详细的中文注释，包括使用场景、参数、返回值、数据流向
 * 
 * 🔄 更新时机：
 * - 后端新增 API 方法
 * - 后端修改参数或响应结构
 * - 后端修改路由路径
 * 
 * ✅ 验证方式：
 * - 确保所有 Controller 的 public 方法都已映射
 * - 确保所有类型导入正确
 * - 确保 URL 路径与后端路由一致
 * - 运行 `npm run type-check` 验证 TypeScript 编译
 * 
 * @module api/sales-order/factory
 * @description SalesOrder 模块 API 工厂函数
 * @generated AI-assisted
 * @author AI Assistant
 * @created 2025-10-19
 * @last-updated 2025-10-19
 * 
 * @change-log
 * - 2025-10-19: 初始创建，实现所有基础 CRUD 方法、订单状态管理、统计查询
 * ============================================================================
 */

// ===== 导入类型定义 =====
import type {
  SalesOrder,                    // 销售订单主实体类型
  SalesOrderQueryParams,          // 列表查询参数
  CreateSalesOrderForm,           // 创建订单表单
  UpdateSalesOrderForm,           // 更新订单表单
  ApproveSalesOrderForm,          // 审核订单表单
  CancelSalesOrderForm,           // 取消订单表单
  SalesOrderGlobalStatistics,     // 全局统计数据
  SalesOrderStatistics,           // 单个订单统计
  SalesOrderStats,                // 订单状态统计（废弃）
} from '../../types/sales-order'

// ===== 导入通用类型 =====
import type { PaginationResult } from '../../types/api'

// ===== 导入统一的请求客户端接口 =====
// ⚠️ 重要：所有工厂函数都从这里导入接口，不要重复定义！
import type { IRequestClient } from '../core/client'

// ===== 导入 API 路由定义 =====
import { API_ROUTES } from '../routes'

// ===== 导出工厂函数 =====
/**
 * 创建 SalesOrder 模块的 API 方法集合
 * 
 * 这是一个工厂函数，它接收一个"请求客户端"作为参数，
 * 返回一个包含所有 SalesOrder 相关 API 方法的对象。
 * 
 * @param client - 请求客户端，必须实现 IRequestClient 接口
 *   - 可以是 Web 端的 axios 适配器
 *   - 可以是 App 端的 uni.request 适配器
 *   - 可以是测试用的 Mock 客户端
 * 
 * @returns API 方法集合对象，包含所有 SalesOrder 相关的请求方法
 * 
 * 为什么这样设计？
 * - 解耦：API 逻辑与具体的请求实现分离
 * - 复用：同一套 API 定义可以在 Web 和 App 端使用
 * - 测试：可以注入 Mock 客户端进行单元测试
 */
export function createSalesOrderApi(client: IRequestClient) {
  return {
    // ===== 基础 CRUD 操作 =====
    
    /**
     * 获取销售订单列表（分页）
     * 
     * 使用场景：
     * - 在销售订单管理页面的主列表中使用
     * - 支持分页、搜索、筛选功能
     * - 用户可以根据订单编号、客户、状态、日期范围等条件查询
     * 
     * @param params - 查询参数对象（可选）
     *   - order_no?: string - 订单编号（精确匹配）
     *     示例：order_no: 'SO2025010001'（查找指定订单号）
     *   - customer_id?: number - 客户ID
     *     示例：customer_id: 123（查找指定客户的订单）
     *   - status?: SalesOrderStatus - 订单状态
     *     示例：status: 1（查找待审核订单）
     *   - priority?: SalesOrderPriority - 优先级
     *     示例：priority: 1（查找紧急订单）
     *   - order_date_start?: string - 订单日期开始
     *     示例：order_date_start: '2025-01-01'
     *   - order_date_end?: string - 订单日期结束
     *     示例：order_date_end: '2025-01-31'
     *   - required_date_start?: string - 要求交期开始
     *   - required_date_end?: string - 要求交期结束
     *   - sales_person?: string - 销售员
     *     示例：sales_person: '张三'
     *   - keyword?: string - 关键字搜索（订单编号、客户名称）
     *     示例：keyword: '客户A'（搜索包含"客户A"的订单）
     *   - per_page?: number - 每页数量，默认 20
     *   - sort_by?: string - 排序字段
     *     示例：sort_by: 'order_date'（按订单日期排序）
     *   - sort_order?: 'asc' | 'desc' - 排序方向
     *     示例：sort_order: 'desc'（降序）
     * 
     * @returns Promise<PaginationResult<SalesOrder>> 分页结果对象
     *   返回的数据结构：
     *   {
     *     data: SalesOrder[],      // 订单列表数组
     *     total: number,           // 总记录数
     *     current_page: number,    // 当前页码
     *     per_page: number,        // 每页数量
     *     last_page: number,       // 最后一页
     *     from: number,            // 当前页起始记录号
     *     to: number               // 当前页结束记录号
     *   }
     * 
     * 数据流向（完整过程）：
     * 1. 用户操作：用户在销售订单列表页面点击"下一页"或输入搜索条件
     * 2. 前端调用：salesOrderApi.getSalesOrderList({ page: 2, status: 1 })
     * 3. 适配器处理：适配器将参数转换为 HTTP 请求
     *    - Web端：axios 发送 GET 请求
     *    - App端：uni.request 发送 GET 请求
     * 4. 网络传输：发送到后端 GET /api/v1/sales/orders?page=2&status=1
     * 5. 后端处理：
     *    - 路由匹配到 SalesOrderController@index 方法
     *    - 验证查询参数（SalesOrderQueryParams）
     *    - 查询数据库，应用筛选条件和分页
     *    - 通过 SalesOrderResource 格式化数据（snake_case → camelCase）
     *    - 返回包含订单详情、客户信息、订单明细的完整数据
     * 6. 响应返回：后端返回 JSON 格式的分页数据
     * 7. 适配器解包：适配器提取 response.data.data（实际数据）
     * 8. 前端接收：组件收到 PaginationResult<SalesOrder> 类型的数据
     * 9. 页面更新：使用数据更新表格显示，显示订单编号、客户、金额、状态等
     * 
     * 代码示例：
     * ```typescript
     * // 示例1：获取第一页（使用默认参数）
     * const page1 = await salesOrderApi.getSalesOrderList()
     * console.log(page1.data)  // 订单对象数组
     * console.log(page1.total) // 总订单数
     * 
     * // 示例2：查询待审核订单
     * const pending = await salesOrderApi.getSalesOrderList({
     *   status: SalesOrderStatus.PENDING
     * })
     * 
     * // 示例3：按客户查询订单
     * const customerOrders = await salesOrderApi.getSalesOrderList({
     *   customer_id: 123
     * })
     * 
     * // 示例4：组合查询（日期范围 + 状态 + 排序）
     * const result = await salesOrderApi.getSalesOrderList({
     *   order_date_start: '2025-01-01',
     *   order_date_end: '2025-01-31',
     *   status: SalesOrderStatus.IN_PRODUCTION,
     *   sort_by: 'order_date',
     *   sort_order: 'desc'
     * })
     * ```
     * 
     * 错误处理：
     * - 404：资源不存在（路由错误或后端未实现）
     * - 422：参数验证失败（日期格式错误等）
     * - 500：服务器内部错误（数据库查询失败等）
     */
    getSalesOrderList(params?: SalesOrderQueryParams) {
      return client.get<PaginationResult<SalesOrder>>(API_ROUTES.salesOrders.list, { params })
    },
    
    /**
     * 获取所有销售订单（不分页）
     * 
     * 使用场景：
     * - 在下拉选择框中选择订单时使用
     * - 导出所有订单数据
     * - 生成订单相关的统计报表
     * 
     * ⚠️ 注意：此方法会返回所有订单，如果订单数量过多可能影响性能
     * 建议：生产环境中谨慎使用，优先使用分页接口
     * 
     * @returns Promise<SalesOrder[]> 所有订单列表
     * 
     * 数据流向：
     * 1. 用户操作：需要获取完整的订单列表（如导出、生成报表）
     * 2. 前端调用：salesOrderApi.getAllSalesOrders()
     * 3. 发送请求：GET /api/v1/sales/orders/all
     * 4. 后端处理：SalesOrderController@all 方法
     *    - 查询所有订单记录（无分页）
     *    - 通过 SalesOrderResource 格式化数据
     * 5. 返回数据：订单数组
     * 6. 前端处理：用于下拉选择或导出
     * 
     * 代码示例：
     * ```typescript
     * // 获取所有订单
     * const allOrders = await salesOrderApi.getAllSalesOrders()
     * console.log(allOrders.length)  // 订单总数
     * 
     * // 在下拉框中使用
     * const orderOptions = allOrders.map(order => ({
     *   label: order.orderNo,
     *   value: order.id
     * }))
     * ```
     * 
     * 错误处理：
     * - 403：无权限访问
     * - 500：服务器错误（数据量过大导致超时）
     */
    getAllSalesOrders() {
      return client.get<SalesOrder[]>(API_ROUTES.salesOrders.orders)  // 注意：需确认all端点
    },
    
    /**
     * 获取销售订单详情
     * 
     * 使用场景：
     * - 在订单详情页面显示完整信息
     * - 编辑订单时获取当前数据
     * - 查看订单的详细资料（包括订单明细、客户信息）
     * 
     * @param id - 订单的唯一标识符（主键）
     *   示例：id: 123（获取 ID 为 123 的订单）
     * 
     * @returns Promise<SalesOrder> 单个订单对象
     *   返回的数据结构：
     *   {
     *     id: number,                    // 订单 ID
     *     orderNo: string,               // 订单编号
     *     customerId: number,            // 客户 ID
     *     orderDate: string,             // 订单日期
     *     requiredDate: string,          // 要求交期
     *     status: SalesOrderStatus,      // 订单状态
     *     priority: SalesOrderPriority,  // 优先级
     *     totalAmount: number,           // 订单总金额
     *     totalQuantity: number,         // 订单总数量
     *     producedQuantity: number,      // 已生产数量
     *     completionPercent: number,     // 完成百分比
     *     customer: {                    // 客户信息（关联加载）
     *       id: number,
     *       customerCode: string,
     *       customerName: string
     *     },
     *     items: [                       // 订单明细（关联加载）
     *       {
     *         id: number,
     *         lineNo: number,
     *         materialId: number,
     *         materialCode: string,
     *         materialName: string,
     *         quantity: number,
     *         unitPrice: number,
     *         subtotal: number,
     *         // ... 其他明细字段
     *       }
     *     ],
     *     // ... 其他订单字段
     *   }
     * 
     * 数据流向：
     * 1. 用户点击列表中的某个订单，或在 URL 中输入详情页地址
     * 2. 前端调用：salesOrderApi.getSalesOrderDetail(123)
     * 3. 发送请求：GET /api/v1/sales/orders/123
     * 4. 后端处理：SalesOrderController@show 方法
     *    - 验证 ID 参数
     *    - 从数据库查询指定 ID 的订单记录
     *    - 预加载关联数据（客户、订单明细）
     *    - 如果不存在，返回 404
     *    - 通过 SalesOrderResource 格式化数据
     * 5. 返回数据：完整的订单对象（包含客户和明细）
     * 6. 前端显示：在详情页展示订单信息、客户信息、订单明细列表
     * 
     * 代码示例：
     * ```typescript
     * // 获取 ID 为 123 的订单
     * const order = await salesOrderApi.getSalesOrderDetail(123)
     * console.log(order.orderNo)        // 订单编号
     * console.log(order.customer.customerName)  // 客户名称
     * console.log(order.items.length)   // 明细行数
     * 
     * // 在详情页组件中使用
     * const { id } = useRoute().params
     * const orderData = await salesOrderApi.getSalesOrderDetail(Number(id))
     * ```
     * 
     * 错误处理：
     * - 404：订单不存在
     * - 403：无权限访问该订单
     * - 422：ID 格式错误（不是数字）
     */
    getSalesOrderDetail(id: number) {
      return client.get<SalesOrder>(API_ROUTES.salesOrders.detail(id))
    },
    
    /**
     * 按订单编号获取销售订单
     * 
     * 使用场景：
     * - 用户输入订单编号快速查找订单
     * - 扫码查询订单（订单编号条形码）
     * - 订单编号搜索功能
     * 
     * @param orderNo - 订单编号
     *   示例：orderNo: 'SO2025010001'
     * 
     * @returns Promise<SalesOrder> 订单对象
     * 
     * 数据流向：
     * 1. 用户在搜索框输入订单编号或扫描条形码
     * 2. 前端调用：salesOrderApi.getSalesOrderByNo('SO2025010001')
     * 3. 发送请求：GET /api/v1/sales/orders/by-order-no/SO2025010001
     * 4. 后端处理：SalesOrderController@getByOrderNo 方法
     *    - 根据订单编号查询订单
     *    - 如果不存在，返回 404
     *    - 返回订单数据
     * 5. 前端处理：显示订单详情或跳转到详情页
     * 
     * 代码示例：
     * ```typescript
     * // 按订单编号查询
     * const order = await salesOrderApi.getSalesOrderByNo('SO2025010001')
     * if (order) {
     *   // 跳转到详情页
     *   router.push(`/sales-order/${order.id}`)
     * }
     * 
     * // 扫码查询
     * const handleScan = async (code: string) => {
     *   try {
     *     const order = await salesOrderApi.getSalesOrderByNo(code)
     *     message.success('找到订单：' + order.orderNo)
     *   } catch (error) {
     *     message.error('未找到订单')
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 404：订单编号不存在
     * - 422：订单编号格式错误
     */
    getSalesOrderByNo(orderNo: string) {
      return client.get<SalesOrder>(API_ROUTES.salesOrders.salesOrders(orderNo))  // 注意：需确认by-order-no端点
    },
    
    /**
     * 按客户ID获取销售订单列表
     * 
     * 使用场景：
     * - 在客户详情页查看该客户的所有订单
     * - 分析客户的订单历史
     * - 客户订单统计和报表
     * 
     * @param customerId - 客户ID
     *   示例：customerId: 123
     * 
     * @returns Promise<SalesOrder[]> 订单列表
     * 
     * 数据流向：
     * 1. 用户在客户详情页点击"订单记录"选项卡
     * 2. 前端调用：salesOrderApi.getSalesOrdersByCustomer(123)
     * 3. 发送请求：GET /api/v1/sales/orders/by-customer/123
     * 4. 后端处理：SalesOrderController@getByCustomer 方法
     *    - 查询该客户的所有订单
     *    - 按订单日期降序排序
     *    - 返回订单列表
     * 5. 前端处理：在客户详情页显示订单列表
     * 
     * 代码示例：
     * ```typescript
     * // 获取客户的所有订单
     * const orders = await salesOrderApi.getSalesOrdersByCustomer(123)
     * console.log(`客户有 ${orders.length} 个订单`)
     * console.log(`总金额：${orders.reduce((sum, o) => sum + o.totalAmount, 0)}`)
     * 
     * // 在客户详情页显示
     * const customerOrders = ref<SalesOrder[]>([])
     * onMounted(async () => {
     *   customerOrders.value = await salesOrderApi.getSalesOrdersByCustomer(customerId)
     * })
     * ```
     * 
     * 错误处理：
     * - 404：客户不存在
     * - 403：无权限查看该客户的订单
     */
    getSalesOrdersByCustomer(customerId: number) {
      return client.get<SalesOrder[]>(API_ROUTES.salesOrders.salesOrders(customerId))  // 注意：需确认by-customer端点
    },
    
    /**
     * 创建新销售订单
     * 
     * 使用场景：
     * - 在销售订单管理页面点击"新建订单"按钮
     * - 提交创建订单的表单
     * - 从报价单转订单
     * 
     * @param data - 创建订单的表单数据
     *   字段要求（参考 CreateSalesOrderForm 类型）：
     *   - order_no?: string - 订单编号（可选，不填则自动生成）
     *   - customer_id: number - 客户ID（必填）
     *   - order_date: string - 订单日期（必填）
     *   - required_date: string - 要求交期（必填）
     *   - promise_date?: string - 承诺交期（可选）
     *   - status?: SalesOrderStatus - 订单状态（默认：待审核）
     *   - priority?: SalesOrderPriority - 优先级（默认：中）
     *   - items: SalesOrderItemForm[] - 订单明细（必填，至少1条）
     *     每个明细包含：
     *     - material_id: number - 物料ID（必填）
     *     - material_code: string - 物料编码（必填）
     *     - material_name: string - 物料名称（必填）
     *     - quantity: number - 数量（必填）
     *     - unit_price: number - 单价（必填）
     *     - unit: string - 单位（必填）
     *     - ... 其他明细字段
     *   - ... 其他可选字段
     * 
     * @returns Promise<SalesOrder> 创建成功后的订单对象（包含生成的 ID 和订单编号）
     * 
     * 数据流向：
     * 1. 用户填写创建表单（客户、订单日期、交期、订单明细等），点击"提交"
     * 2. 前端验证：表单验证（必填项、日期范围、明细数量等）
     * 3. 前端调用：salesOrderApi.createSalesOrder({ customer_id: 123, items: [...] })
     * 4. 发送请求：POST /api/v1/sales/orders
     *    请求体：订单数据（JSON格式，snake_case字段）
     * 5. 后端处理：SalesOrderController@store 方法
     *    - 验证数据（使用 CreateSalesOrderRequest）
     *    - 验证客户是否存在且状态正常
     *    - 如果未提供订单编号，自动生成
     *    - 创建订单主记录
     *    - 创建订单明细记录
     *    - 计算订单金额和数量
     *    - 返回新创建的订单（包含关联数据）
     * 6. 前端处理：
     *    - 显示成功提示
     *    - 跳转到订单详情页
     *    - 刷新订单列表
     * 
     * 代码示例：
     * ```typescript
     * // 创建新订单
     * const newOrder: CreateSalesOrderForm = {
     *   customer_id: 123,
     *   order_date: '2025-01-15',
     *   required_date: '2025-02-15',
     *   promise_date: '2025-02-10',
     *   priority: SalesOrderPriority.HIGH,
     *   contact_person: '张三',
     *   contact_phone: '13800138000',
     *   items: [
     *     {
     *       material_id: 1,
     *       material_code: 'M001',
     *       material_name: '产品A',
     *       unit: '个',
     *       quantity: 100,
     *       unit_price: 50.00,
     *       discount_rate: 5,
     *       tax_rate: 13
     *     },
     *     // ... 更多明细
     *   ]
     * }
     * 
     * const created = await salesOrderApi.createSalesOrder(newOrder)
     * console.log(created.orderNo)  // 自动生成的订单编号
     * console.log(created.totalAmount)  // 自动计算的总金额
     * 
     * // 在表单提交中使用
     * const handleSubmit = async (formData: CreateSalesOrderForm) => {
     *   try {
     *     const result = await salesOrderApi.createSalesOrder(formData)
     *     message.success('订单创建成功，订单编号：' + result.orderNo)
     *     router.push(`/sales-order/${result.id}`)
     *   } catch (error) {
     *     message.error('创建失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 422：数据验证失败
     *   - 必填字段缺失（客户、日期、明细等）
     *   - 日期范围错误（要求交期早于订单日期）
     *   - 明细数据错误（数量为负、单价为0等）
     *   - 订单编号重复（手动指定时）
     * - 400：业务规则错误
     *   - 客户状态异常（已禁用、已列入黑名单）
     *   - 物料不存在或已停用
     * - 403：无权限创建订单
     * - 500：服务器错误（数据库插入失败）
     */
    createSalesOrder(data: CreateSalesOrderForm) {
      return client.post<SalesOrder>(API_ROUTES.salesOrders.orders, data)
    },
    
    /**
     * 更新销售订单
     * 
     * 使用场景：
     * - 在订单编辑页面修改后提交
     * - 更新订单的部分字段（如交期、备注）
     * - 修改订单明细
     * 
     * ⚠️ 注意：只有待审核和已确认状态的订单可以编辑
     * 
     * @param id - 要更新的订单的 ID
     * @param data - 更新的数据（只需要包含要修改的字段）
     *   字段要求（参考 UpdateSalesOrderForm 类型）：
     *   - 所有字段都是可选的（部分更新）
     *   - 不能修改的字段：id, order_no, createdAt（后端会忽略）
     *   - items?: SalesOrderItemForm[] - 如果提供，会删除旧明细，创建新明细
     * 
     * @returns Promise<SalesOrder> 更新后的完整订单对象
     * 
     * 数据流向：
     * 1. 用户在编辑页面修改字段，点击"保存"
     * 2. 前端调用：salesOrderApi.updateSalesOrder(123, { required_date: '2025-03-01' })
     * 3. 发送请求：PUT /api/v1/sales/orders/123
     *    请求体：更新数据（只包含修改的字段）
     * 4. 后端处理：SalesOrderController@update 方法
     *    - 验证 ID 是否存在
     *    - 检查订单状态是否可编辑
     *    - 验证更新数据（使用 UpdateSalesOrderRequest）
     *    - 如果包含 items，删除旧明细，创建新明细
     *    - 更新订单主记录
     *    - 重新计算订单金额和数量
     *    - 返回更新后的完整数据
     * 5. 前端处理：
     *    - 显示成功提示
     *    - 更新页面显示的数据
     * 
     * 代码示例：
     * ```typescript
     * // 更新交期
     * const updated = await salesOrderApi.updateSalesOrder(123, {
     *   required_date: '2025-03-01',
     *   promise_date: '2025-02-25'
     * })
     * 
     * // 更新多个字段
     * const updated2 = await salesOrderApi.updateSalesOrder(123, {
     *   priority: SalesOrderPriority.URGENT,
     *   delivery_address: '新地址',
     *   remark: '加急处理'
     * })
     * 
     * // 更新订单明细（会替换所有明细）
     * const updated3 = await salesOrderApi.updateSalesOrder(123, {
     *   items: [
     *     // 新的明细列表
     *   ]
     * })
     * ```
     * 
     * 错误处理：
     * - 404：订单不存在
     * - 422：数据验证失败
     * - 403：无权限修改
     * - 400：订单状态不允许编辑（生产中、已完成、已取消）
     * - 409：冲突（订单编号重复）
     */
    updateSalesOrder(id: number, data: UpdateSalesOrderForm) {
      return client.put<SalesOrder>(API_ROUTES.salesOrders.salesOrders(id), data)
    },
    
    /**
     * 删除销售订单
     * 
     * 使用场景：
     * - 在订单列表中点击"删除"按钮
     * - 在订单详情页删除当前订单
     * - 清理错误创建的订单
     * 
     * ⚠️ 注意：
     * - 生产中的订单不能删除
     * - 已完成和已取消的订单可以删除（软删除，可恢复）
     * 
     * @param id - 要删除的订单的 ID
     * 
     * @returns Promise<void> 删除成功不返回数据
     * 
     * 数据流向：
     * 1. 用户点击删除按钮，弹出确认对话框
     * 2. 用户确认删除
     * 3. 前端调用：salesOrderApi.deleteSalesOrder(123)
     * 4. 发送请求：DELETE /api/v1/sales/orders/123
     * 5. 后端处理：SalesOrderController@destroy 方法
     *    - 验证 ID 是否存在
     *    - 检查订单状态是否允许删除
     *    - 软删除订单记录（设置 deleted_at）
     *    - 级联软删除订单明细
     *    - 返回成功响应
     * 6. 前端处理：
     *    - 显示成功提示
     *    - 从列表中移除该记录
     *    - 刷新列表数据
     * 
     * 代码示例：
     * ```typescript
     * // 删除指定订单
     * await salesOrderApi.deleteSalesOrder(123)
     * console.log('删除成功')
     * 
     * // 在列表中使用（带确认）
     * const handleDelete = async (id: number) => {
     *   const confirmed = await showConfirm('确定要删除这个订单吗？')
     *   if (!confirmed) return
     *   
     *   try {
     *     await salesOrderApi.deleteSalesOrder(id)
     *     message.success('删除成功')
     *     // 刷新列表
     *     await loadList()
     *   } catch (error) {
     *     message.error('删除失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 404：订单不存在（可能已被删除）
     * - 403：无权限删除
     * - 409：无法删除（订单处于生产中状态）
     */
    deleteSalesOrder(id: number) {
      return client.delete<void>(API_ROUTES.salesOrders.delete(id))
    },
    
    // ===== 订单状态管理 =====
    
    /**
     * 审核销售订单
     * 
     * 使用场景：
     * - 在待审核订单列表点击"审核"按钮
     * - 在订单详情页审核通过订单
     * - 批量审核多个订单（需要循环调用）
     * 
     * ⚠️ 注意：只有待审核状态的订单可以审核
     * 
     * @param id - 订单ID
     * @param data - 审核数据（可选）
     *   - note?: string - 审核备注
     * 
     * @returns Promise<SalesOrder> 审核后的订单信息
     * 
     * 数据流向：
     * 1. 用户在待审核订单列表点击"审核"，填写审核意见
     * 2. 前端调用：salesOrderApi.approveSalesOrder(123, { note: '同意' })
     * 3. 发送请求：POST /api/v1/sales/orders/123/approve
     * 4. 后端处理：SalesOrderController@approve 方法
     *    - 验证订单是否存在
     *    - 检查订单状态是否为待审核
     *    - 更新订单状态为已确认
     *    - 记录审核人和审核时间
     *    - 保存审核备注
     *    - 返回更新后的订单
     * 5. 前端处理：
     *    - 显示"审核成功"提示
     *    - 更新订单列表（从待审核移除）
     *    - 刷新统计数据
     * 
     * 代码示例：
     * ```typescript
     * // 审核订单（不带备注）
     * const approved = await salesOrderApi.approveSalesOrder(123)
     * 
     * // 审核订单（带备注）
     * const approved2 = await salesOrderApi.approveSalesOrder(123, {
     *   note: '客户信用良好，同意接单'
     * })
     * 
     * // 在审核弹窗中使用
     * const handleApprove = async (id: number, note: string) => {
     *   try {
     *     const result = await salesOrderApi.approveSalesOrder(id, { note })
     *     message.success('审核通过')
     *     // 刷新列表
     *     await loadPendingOrders()
     *   } catch (error) {
     *     message.error('审核失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 404：订单不存在
     * - 403：无权限审核
     * - 400：订单状态不允许审核（已确认、生产中等）
     */
    approveSalesOrder(id: number, data?: ApproveSalesOrderForm) {
      return client.put<SalesOrder>(API_ROUTES.salesOrders.approveApplication(id), data)
    },
    
    /**
     * 取消销售订单
     * 
     * 使用场景：
     * - 客户取消订单
     * - 订单信息错误需要作废
     * - 无法按时交付需要取消
     * 
     * ⚠️ 注意：
     * - 只有待审核和已确认状态的订单可以取消
     * - 取消订单必须填写取消原因
     * 
     * @param id - 订单ID
     * @param data - 取消数据
     *   - reason: string - 取消原因（必填）
     * 
     * @returns Promise<SalesOrder> 取消后的订单信息
     * 
     * 数据流向：
     * 1. 用户点击"取消订单"，填写取消原因
     * 2. 前端调用：salesOrderApi.cancelSalesOrder(123, { reason: '客户取消' })
     * 3. 发送请求：POST /api/v1/sales/orders/123/cancel
     * 4. 后端处理：SalesOrderController@cancel 方法
     *    - 验证订单是否存在
     *    - 检查订单状态是否允许取消
     *    - 更新订单状态为已取消
     *    - 记录取消人、取消时间和取消原因
     *    - 返回更新后的订单
     * 5. 前端处理：
     *    - 显示"订单已取消"提示
     *    - 更新订单显示（状态改为已取消）
     * 
     * 代码示例：
     * ```typescript
     * // 取消订单
     * const cancelled = await salesOrderApi.cancelSalesOrder(123, {
     *   reason: '客户要求取消订单'
     * })
     * 
     * // 在取消弹窗中使用
     * const handleCancel = async (id: number) => {
     *   const reason = await prompt('请输入取消原因')
     *   if (!reason) return
     *   
     *   try {
     *     await salesOrderApi.cancelSalesOrder(id, { reason })
     *     message.success('订单已取消')
     *     await loadList()
     *   } catch (error) {
     *     message.error('取消失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 404：订单不存在
     * - 403：无权限取消
     * - 400：订单状态不允许取消（生产中、已完成、已取消）
     * - 422：取消原因为空或格式错误
     */
    cancelSalesOrder(id: number, data: CancelSalesOrderForm) {
      return client.put<SalesOrder>(API_ROUTES.salesOrders.cancel(id), data)  // 后端使用PUT方法
    },
    
    /**
     * 完成销售订单
     * 
     * 使用场景：
     * - 订单所有明细都已生产完成
     * - 订单已全部发货
     * - 手动标记订单完成
     * 
     * ⚠️ 注意：只有生产中且已生产数量 >= 订单数量的订单可以完成
     * 
     * @param id - 订单ID
     * 
     * @returns Promise<SalesOrder> 完成后的订单信息
     * 
     * 数据流向：
     * 1. 用户在订单详情页点击"标记完成"
     * 2. 前端调用：salesOrderApi.completeSalesOrder(123)
     * 3. 发送请求：POST /api/v1/sales/orders/123/complete
     * 4. 后端处理：SalesOrderController@complete 方法
     *    - 验证订单是否存在
     *    - 检查订单状态是否为生产中
     *    - 检查已生产数量是否满足要求
     *    - 更新订单状态为已完成
     *    - 记录完成人和完成时间
     *    - 返回更新后的订单
     * 5. 前端处理：
     *    - 显示"订单已完成"提示
     *    - 更新订单显示（状态改为已完成）
     * 
     * 代码示例：
     * ```typescript
     * // 完成订单
     * const completed = await salesOrderApi.completeSalesOrder(123)
     * console.log(completed.completedAt)  // 完成时间
     * 
     * // 检查是否可完成后再调用
     * if (order.producedQuantity >= order.totalQuantity) {
     *   try {
     *     await salesOrderApi.completeSalesOrder(order.id)
     *     message.success('订单已完成')
     *   } catch (error) {
     *     message.error('完成失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 404：订单不存在
     * - 403：无权限完成
     * - 400：订单状态不允许完成或生产数量不足
     */
    completeSalesOrder(id: number) {
      return client.post<SalesOrder>(API_ROUTES.salesOrders.salesOrders(id))  // 注意：需确认complete端点
    },
    
    // ===== 特殊查询 =====
    
    /**
     * 获取待审核订单列表
     * 
     * 使用场景：
     * - 在"待审核订单"页面显示所有待审核的订单
     * - 审核人员快速查看需要处理的订单
     * - 首页显示待审核订单数量
     * 
     * @returns Promise<SalesOrder[]> 待审核订单列表
     * 
     * 数据流向：
     * 1. 用户打开"待审核订单"页面
     * 2. 前端调用：salesOrderApi.getPendingSalesOrders()
     * 3. 发送请求：GET /api/v1/sales/orders/pending
     * 4. 后端处理：SalesOrderController@getPending 方法
     *    - 查询状态为待审核的订单
     *    - 按订单日期降序排序
     *    - 返回订单列表
     * 5. 前端显示：在待审核列表中显示
     * 
     * 代码示例：
     * ```typescript
     * // 获取待审核订单
     * const pendingOrders = await salesOrderApi.getPendingSalesOrders()
     * console.log(`有 ${pendingOrders.length} 个订单待审核`)
     * 
     * // 在首页显示待审核数量
     * const pendingCount = ref(0)
     * onMounted(async () => {
     *   const orders = await salesOrderApi.getPendingSalesOrders()
     *   pendingCount.value = orders.length
     * })
     * ```
     * 
     * 错误处理：
     * - 403：无权限查看待审核订单
     */
    getPendingSalesOrders() {
      return client.get<SalesOrder[]>(API_ROUTES.salesOrders.orders)  // 注意：需确认pending端点
    },
    
    /**
     * 获取逾期订单列表
     * 
     * 使用场景：
     * - 在"逾期订单"页面显示所有逾期的订单
     * - 提醒生产部门优先处理逾期订单
     * - 逾期订单预警和统计
     * 
     * ⚠️ 注意：逾期是指要求交期已过且订单未完成
     * 
     * @returns Promise<SalesOrder[]> 逾期订单列表
     * 
     * 数据流向：
     * 1. 用户打开"逾期订单"页面或查看预警
     * 2. 前端调用：salesOrderApi.getOverdueSalesOrders()
     * 3. 发送请求：GET /api/v1/sales/orders/overdue
     * 4. 后端处理：SalesOrderController@getOverdue 方法
     *    - 查询要求交期 < 当前日期 且状态未完成的订单
     *    - 按逾期天数降序排序（最紧急的在前）
     *    - 返回订单列表
     * 5. 前端显示：在逾期列表中显示，标红预警
     * 
     * 代码示例：
     * ```typescript
     * // 获取逾期订单
     * const overdueOrders = await salesOrderApi.getOverdueSalesOrders()
     * console.log(`有 ${overdueOrders.length} 个订单逾期`)
     * 
     * // 计算逾期天数并排序
     * const sortedOrders = overdueOrders.map(order => ({
     *   ...order,
     *   overdueDays: dayjs().diff(order.requiredDate, 'day')
     * })).sort((a, b) => b.overdueDays - a.overdueDays)
     * ```
     * 
     * 错误处理：
     * - 403：无权限查看逾期订单
     */
    getOverdueSalesOrders() {
      return client.get<SalesOrder[]>(API_ROUTES.salesOrders.orders)  // 注意：需确认overdue端点
    },
    
    // ===== 统计相关 =====
    
    /**
     * 获取销售订单全局统计信息
     * 
     * 使用场景：
     * - 在销售订单管理首页显示统计数据
     * - 生成销售报表和仪表盘
     * - 数据分析和决策支持
     * 
     * @returns Promise<SalesOrderGlobalStatistics> 全局统计数据
     *   返回的数据结构：
     *   {
     *     totalOrders: number,           // 订单总数
     *     byStatus: {                    // 按状态分组
     *       1: number,                   // 待审核订单数
     *       2: number,                   // 已确认订单数
     *       3: number,                   // 生产中订单数
     *       4: number,                   // 已完成订单数
     *       5: number                    // 已取消订单数
     *     },
     *     byPriority: {                  // 按优先级分组
     *       1: number,                   // 紧急订单数
     *       2: number,                   // 高优先级订单数
     *       3: number,                   // 中优先级订单数
     *       4: number                    // 低优先级订单数
     *     },
     *     totalAmount: number,           // 订单总金额
     *     paidAmount: number,            // 已付总金额
     *     unpaidAmount: number,          // 未付总金额
     *     avgOrderAmount: number,        // 平均订单金额
     *     todayOrders: number,           // 今日新增订单数
     *     thisMonthOrders: number,       // 本月新增订单数
     *     totalQuantity: number,         // 订单总数量
     *     producedQuantity: number,      // 已生产总数量
     *     shippedQuantity: number        // 已发货总数量
     *   }
     * 
     * 数据流向：
     * 1. 用户打开销售订单管理首页
     * 2. 前端调用：salesOrderApi.getSalesOrderGlobalStatistics()
     * 3. 发送请求：GET /api/v1/sales/orders/statistics
     * 4. 后端处理：SalesOrderController@statistics 方法
     *    - 统计所有订单的各项指标
     *    - 按状态、优先级分组统计
     *    - 计算金额、数量等汇总数据
     *    - 返回统计结果
     * 5. 前端显示：在仪表盘显示各项统计数据
     * 
     * 代码示例：
     * ```typescript
     * // 获取全局统计
     * const stats = await salesOrderApi.getSalesOrderGlobalStatistics()
     * console.log(`总订单数：${stats.totalOrders}`)
     * console.log(`订单总金额：${stats.totalAmount}`)
     * console.log(`待审核：${stats.byStatus[1]}`)
     * console.log(`今日新增：${stats.todayOrders}`)
     * 
     * // 在仪表盘组件中使用
     * const statistics = ref<SalesOrderGlobalStatistics | null>(null)
     * onMounted(async () => {
     *   statistics.value = await salesOrderApi.getSalesOrderGlobalStatistics()
     * })
     * 
     * // 计算完成率
     * const completionRate = computed(() => {
     *   if (!statistics.value) return 0
     *   const { totalQuantity, producedQuantity } = statistics.value
     *   return totalQuantity > 0 ? (producedQuantity / totalQuantity * 100).toFixed(2) : 0
     * })
     * ```
     * 
     * 错误处理：
     * - 403：无权限查看统计数据
     * - 500：统计计算错误
     */
    getSalesOrderGlobalStatistics() {
      return client.get<SalesOrderGlobalStatistics>(API_ROUTES.salesOrders.statistics)
    },
    
    /**
     * 获取单个订单统计信息
     * 
     * 使用场景：
     * - 在订单详情页显示订单的统计数据
     * - 查看订单的生产进度和交付进度
     * - 分析单个订单的完成情况
     * 
     * @param id - 订单ID
     * 
     * @returns Promise<SalesOrderStatistics> 订单统计数据
     *   返回的数据结构：
     *   {
     *     orderNo: string,              // 订单编号
     *     customerName: string,         // 客户名称
     *     itemCount: number,            // 明细条目数
     *     totalQuantity: number,        // 订单总数量
     *     totalAmount: number,          // 订单总金额
     *     status: number,               // 订单状态
     *     productionProgress: number,   // 生产进度（%）
     *     deliveryProgress: number      // 交付进度（%）
     *   }
     * 
     * 数据流向：
     * 1. 用户打开订单详情页
     * 2. 前端调用：salesOrderApi.getSalesOrderStatistics(123)
     * 3. 发送请求：GET /api/v1/sales/orders/123/statistics
     * 4. 后端处理：SalesOrderController@orderStatistics 方法
     *    - 查询指定订单
     *    - 统计订单明细数量
     *    - 计算生产进度和交付进度
     *    - 返回统计结果
     * 5. 前端显示：在订单详情页显示统计信息
     * 
     * 代码示例：
     * ```typescript
     * // 获取订单统计
     * const stats = await salesOrderApi.getSalesOrderStatistics(123)
     * console.log(`订单 ${stats.orderNo}`)
     * console.log(`客户：${stats.customerName}`)
     * console.log(`生产进度：${stats.productionProgress}%`)
     * console.log(`交付进度：${stats.deliveryProgress}%`)
     * 
     * // 在订单详情页显示进度条
     * <a-progress :percent="stats.productionProgress" />
     * ```
     * 
     * 错误处理：
     * - 404：订单不存在
     * - 403：无权限查看订单统计
     */
    getSalesOrderStatistics(id: number) {
      return client.get<SalesOrderStatistics>(API_ROUTES.salesOrders.ordersStatistics(id))
    },
    
    /**
     * 获取订单状态统计（废弃）
     * 
     * @deprecated 此方法已废弃，请使用 getSalesOrderGlobalStatistics
     * 
     * 使用场景：
     * - 兼容旧代码
     * - 建议迁移到 getSalesOrderGlobalStatistics
     * 
     * @returns Promise<SalesOrderStats> 状态统计数据
     */
    getSalesOrderStats() {
      return client.get<SalesOrderStats>(API_ROUTES.salesOrders.statistics)  // stats映射到statistics
    },
  }
}

